import {
  CATEGORIES,
  DAILY_KINDS,
  DEFAULT_WEIGHTS,
  type DailyKind,
  type DailyRepickResult,
  type GeneratorAdminResponse,
  type GeneratorSettings,
  type JobDto,
  type QualityFeatures,
} from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { libraryKeys } from '../../lib/library';
import { Bar, Panel, SmallSwitch, smallButton, tableCell } from './parts';

const FEATURE_NAMES: Record<keyof QualityFeatures, string> = {
  breakIn: 'Clear break-in',
  noGuessing: 'No guessing',
  variantWork: 'Rules do the work',
  fewGivens: 'Few givens',
  loadBearing: 'Every clue used',
  steadyCurve: 'Steady difficulty',
  visual: 'Tidy look',
};

const input =
  'h-10 w-24 rounded-btn border border-line bg-surface px-3 font-sans text-[14px] text-ink';

function repickMessage(job: JobDto): string {
  if (job.state === 'failed') return job.error ?? 'Picking again failed.';
  if (job.state !== 'done') return 'Picking…';
  const r = job.result as DailyRepickResult;
  const score = (n: number | null) => (n === null ? 'unrated' : n.toFixed(1));
  if ('picked' in r.result) {
    const now = `Today's ${r.kind} is now “${r.result.picked.title}” (${score(r.result.picked.score)}).`;
    return r.replaced
      ? `${now} It replaced “${r.replaced.title}” (${score(r.replaced.score)}), which stays in the library.`
      : now;
  }
  return r.replaced
    ? `Nothing under the hardest score to pick, so today's ${r.kind} stays “${r.replaced.title}”.`
    : `Nothing under the hardest score to pick for today's ${r.kind}.`;
}

/** "Pick again": today's daily of one kind, replaced under the saved hardest score (Marko,
 * 2026-11-01). The worker does it; the job's result says what happened. */
function RepickDailies() {
  const client = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api<JobDto>(`/api/admin/jobs/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (q) =>
      q.state.data && ['done', 'failed'].includes(q.state.data.state) ? false : 1000,
  });
  const start = useMutation({
    mutationFn: (kind: DailyKind) =>
      api<{ jobId: string }>(`/api/admin/generator/daily/${kind}/repick`, { method: 'POST' }),
    onSuccess: (r) => setJobId(r.jobId),
  });
  const done = job.data?.state === 'done';
  useEffect(() => {
    if (done) void client.invalidateQueries({ queryKey: libraryKeys.home });
  }, [done, client]);
  const busy =
    start.isPending || (job.data !== undefined && !['done', 'failed'].includes(job.data.state));
  return (
    <div className="flex flex-col gap-2 border-t border-line pt-3">
      <span className="text-[14px] font-semibold">Today&apos;s dailies</span>
      <div className="flex flex-wrap gap-2">
        {DAILY_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className={smallButton}
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  `Pick a new ${kind} daily for today, no harder than the saved hardest score? The current one stays in the library.`,
                )
              )
                start.mutate(kind);
            }}
          >
            Pick the {kind} again
          </button>
        ))}
      </div>
      {start.isError && (
        <p role="alert" className="m-0 text-[13px] text-mistake">
          {start.error.message}
        </p>
      )}
      {job.data && (
        <p role="status" className="m-0 text-[13px] text-muted">
          {repickMessage(job.data)}
        </p>
      )}
    </div>
  );
}

/** Admin → Generator: pool size and categories, difficulty range, daily puzzle time, hand-set
 * share, backlog pause, CPU limit, pause or run now; the tuned quality weights. */
export function AdminGeneratorPage() {
  const client = useQueryClient();
  const state = useQuery({
    queryKey: ['admin', 'generator'],
    queryFn: () => api<GeneratorAdminResponse>('/api/admin/generator'),
    refetchInterval: 10_000,
  });
  const save = useMutation({
    mutationFn: (patch: Partial<GeneratorSettings>) =>
      api<GeneratorAdminResponse>('/api/admin/generator', { method: 'PUT', json: patch }),
    onSuccess: (data) => {
      client.setQueryData(['admin', 'generator'], data);
      void client.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
  const run = useMutation({
    mutationFn: () => api<GeneratorAdminResponse>('/api/admin/generator/run', { method: 'POST' }),
    onSuccess: (data) => client.setQueryData(['admin', 'generator'], data),
  });
  const s = state.data?.settings;
  // The fields show the settings, or what's been typed since the last Save.
  const [edits, setEdits] = useState<Record<string, string>>({});
  if (!s) return <h1 className="m-0 text-[26px] font-bold">Generator</h1>;
  const shown: Record<string, string> = {
    poolSize: String(s.poolSize),
    minScore: String(s.minScore),
    maxScore: String(s.maxScore),
    dailyTime: s.dailyTime,
    handSetRatio: String(Math.round(s.handSetRatio * 100)),
    backlog: String(s.backlog),
    dailyMaxClassic: String(s.dailyMaxClassic),
    dailyMaxVariant: String(s.dailyMaxVariant),
    cpuLimit: String(Math.round(s.cpuLimit * 100)),
  };
  const form = { ...shown, ...edits };
  const field = (key: string, label: string, suffix?: string) => (
    <label className="flex items-center gap-2 text-[14px]">
      <span className="w-48">{label}</span>
      <input
        aria-label={label}
        value={form[key] ?? ''}
        onChange={(e) => setEdits((f) => ({ ...f, [key]: e.target.value }))}
        className={input}
      />
      {suffix && <span className="text-muted">{suffix}</span>}
    </label>
  );
  const submit = () =>
    save.mutate(
      {
        poolSize: Number(form.poolSize),
        minScore: Number(form.minScore),
        maxScore: Number(form.maxScore),
        dailyTime: form.dailyTime,
        handSetRatio: Number(form.handSetRatio) / 100,
        backlog: Number(form.backlog),
        dailyMaxClassic: Number(form.dailyMaxClassic),
        dailyMaxVariant: Number(form.dailyMaxVariant),
        cpuLimit: Number(form.cpuLimit) / 100,
      },
      { onSuccess: () => setEdits({}) },
    );
  const refill = state.data?.refill;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="m-0 text-[26px] font-bold">Generator</h1>
        <div className="flex items-center gap-2">
          <span className="text-[14px]">{s.paused ? 'Paused' : 'Running'}</span>
          <SmallSwitch
            checked={!s.paused}
            label="Generator on"
            onChange={(on) => save.mutate({ paused: !on })}
          />
          <button
            type="button"
            className={smallButton}
            disabled={s.paused || run.isPending || refill != null}
            onClick={() => run.mutate()}
          >
            Run now
          </button>
          <Link to="/admin/pool" className={smallButton}>
            Browse pool
          </Link>
        </div>
      </div>
      {refill && (
        <Panel title="Refilling the pool" small>
          <Bar value={refill.progress} />
          <span className="text-[13px] text-muted">
            {refill.state === 'running' ? 'Running' : 'Waiting its turn'} · nightly 01:00–05:00, and
            after the Generate button takes from the pool
          </span>
        </Panel>
      )}
      {(save.isError || run.isError) && (
        <p role="alert" className="m-0 text-[14px] text-mistake">
          {(save.error ?? run.error)?.message}
        </p>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Pool">
          <form
            className="flex flex-col gap-2.5"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {field('poolSize', 'Puzzles per category')}
            {field('minScore', 'Lowest score')}
            {field('maxScore', 'Highest score')}
            {field('cpuLimit', 'CPU limit', '% of the cores, low priority')}
            <h3 className="m-0 pt-1 text-[14px] font-semibold">Daily puzzle</h3>
            {field('dailyTime', 'Time (Belgrade)')}
            {field('handSetRatio', 'Hand-set days', '%')}
            {field('backlog', 'Pause at unsolved dailies')}
            {field('dailyMaxClassic', 'Hardest classic daily', 'score, 0–10')}
            {field('dailyMaxVariant', 'Hardest variant daily', 'score, 10 = no limit')}
            <button type="submit" className={`${smallButton} w-fit`} disabled={save.isPending}>
              Save
            </button>
          </form>
          <RepickDailies />
        </Panel>
        <Panel title="Categories">
          <table className="w-full border-collapse text-[13px]">
            <tbody>
              {CATEGORIES.map((c) => {
                const on = s.categories.includes(c.key);
                return (
                  <tr key={c.key}>
                    <td className={`${tableCell} font-semibold`}>{c.name}</td>
                    <td className={`${tableCell} text-muted`}>
                      {c.types.length === 0
                        ? 'givens only'
                        : `${c.types.length} rule ${c.types.length === 1 ? 'type' : 'types'}`}
                    </td>
                    <td className={`${tableCell} text-right`}>
                      <SmallSwitch
                        checked={on}
                        label={`${c.name} on`}
                        onChange={(next) =>
                          save.mutate({
                            categories: next
                              ? [...s.categories, c.key]
                              : s.categories.filter((k) => k !== c.key),
                          })
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
        <Panel title="Quality weights">
          <p className="m-0 text-[13px] text-muted">
            Tuned from thumbs up and down on generated puzzles ({state.data?.ratings.up ?? 0} up,{' '}
            {state.data?.ratings.down ?? 0} down; at least 3 of each before they move).
          </p>
          <table className="w-full border-collapse text-[13px]">
            <tbody>
              {(Object.keys(FEATURE_NAMES) as (keyof QualityFeatures)[]).map((k) => (
                <tr key={k}>
                  <td className={tableCell}>{FEATURE_NAMES[k]}</td>
                  <td className={`${tableCell} text-right tabular-nums`}>
                    {s.weights[k].toFixed(2)}
                  </td>
                  <td className={`${tableCell} text-right text-muted tabular-nums`}>
                    default {DEFAULT_WEIGHTS[k].toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </>
  );
}
